import type { FetchRequestError } from "@lib/errors";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type {
  SHARED_CONSTANTS_AGENTIC_VALIDATION_ERROR_CODES,
  SHARED_CONSTANTS_AMAZON_BEDROCK_AUTH_TYPES,
} from "@lib/shared_constants";

type AgenticValidationErrorCode =
  SHARED_CONSTANTS_AGENTIC_VALIDATION_ERROR_CODES[keyof SHARED_CONSTANTS_AGENTIC_VALIDATION_ERROR_CODES];

/**
 * Discriminated union with one FetchUserEncodedError member per backend
 * agentic validation code, so `switch (error.errorCode)` narrows and an
 * exhaustiveCheck default branch fails to compile when the backend adds a
 * code without a frontend handler.
 */
export type PostAlloysProviderAgenticErrors = {
  [Code in AgenticValidationErrorCode]: FetchUserEncodedError<Code>;
}[AgenticValidationErrorCode];

export const isPostAlloysProviderAgenticError = (
  error: FetchRequestError
): error is PostAlloysProviderAgenticErrors => {
  return error instanceof FetchUserEncodedError;
};

export interface AlloysAgenticIntegration {
  company_id: string;
  app_group_id: string;
  integration_id: string;
  api_key: string | null;
  connected_at: string | null;
}

export interface GeminiAgenticIntegration extends AlloysAgenticIntegration {
  use_vertex_ai: boolean;
  vertex_project_id: string | null;
  use_oauth?: boolean;
  google_account_email?: string | null;
  oauth_connected_at?: string | null;
  oauth_project_id?: string | null;
  oauth_project_name?: string | null;
  oauth_connected?: boolean;
  oauth_pending?: boolean;
}

export interface DatabricksMosaicAgenticIntegration extends AlloysAgenticIntegration {
  workspace_name: string;
}

export interface MicrosoftFoundryAgenticIntegration extends AlloysAgenticIntegration {
  project_name: string;
}

/**
 * 1 = API key auth, 2 = IAM role auth, derived from the generated
 * AMAZON_BEDROCK_AUTH_TYPES shared constant
 * (Alloys::Agentic::AmazonBedrockPartnerIntegration::AUTH_TYPES_BY_NAME)
 */
export type AmazonBedrockAuthType =
  SHARED_CONSTANTS_AMAZON_BEDROCK_AUTH_TYPES[keyof SHARED_CONSTANTS_AMAZON_BEDROCK_AUTH_TYPES];

export interface AmazonBedrockAgenticIntegration extends AlloysAgenticIntegration {
  aws_region: string;
  auth_type?: AmazonBedrockAuthType;
  role_arn?: string | null;
  external_id?: string | null;
}

export interface GetAlloysAmazonBedrockAgenticExternalIdResult {
  external_id: string;
  account_id: string;
}

export interface GetAlloysProviderAgenticAppGroupIdResult {
  integration:
    | AlloysAgenticIntegration
    | GeminiAgenticIntegration
    | DatabricksMosaicAgenticIntegration
    | MicrosoftFoundryAgenticIntegration
    | AmazonBedrockAgenticIntegration;
}

export type DeleteAlloysProviderAgenticAppGroupIdResult = Record<string, never>;

export interface DeleteAlloysProviderAgenticAppGroupIdProps {
  provider: string;
  appGroupId: string;
}

export interface PostAlloysProviderAgenticAppGroupIdResult {
  integration:
    | AlloysAgenticIntegration
    | GeminiAgenticIntegration
    | DatabricksMosaicAgenticIntegration
    | MicrosoftFoundryAgenticIntegration
    | AmazonBedrockAgenticIntegration;
}

export interface PostAlloysProviderAgenticAppGroupIdProps {
  provider: string;
  appGroupId: string;
  apiKey: string;
  useVertexAi?: boolean;
  vertexProjectId?: string;
  workspaceName?: string;
  projectName?: string;
  awsRegion?: string;
  authType?: AmazonBedrockAuthType;
  roleArn?: string;
  externalId?: string;
}
